/** Shared building blocks for the procedural generators (pure TypeScript, no Web Audio). */

/** Three normalised (0..1) character controls; meaning depends on family. */
export interface GeneratorParams {
  a: number
  b: number
  c: number
}

export interface Generator {
  process(out: Float32Array, p: GeneratorParams): void
}
export class Rng {
  private s: number
  constructor(seed: number) {
    this.s = seed >>> 0 || 0x9e3779b9
  }
  /** mulberry32 → [0, 1) */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0
    let t = this.s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
  /** Uniform in [-1, 1). */
  bipolar(): number {
    return this.next() * 2 - 1
  }
}

export const TWO_PI = Math.PI * 2
export const CONTROL_PERIOD = 32

export const onePoleCoef = (seconds: number, sr: number) => 1 - Math.exp(-1 / Math.max(1, seconds * sr))
export const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x)

/** Smoothly wandering random value in [-1, 1]; picks a new target at a (jittered) rate. */
export class Wander {
  private v = 0
  private target = 0
  private left = 0
  private coef: number
  constructor(
    private rng: Rng,
    private sr: number,
    private periodSec: number,
    smoothSec = periodSec * 0.7,
  ) {
    this.coef = onePoleCoef(smoothSec, sr)
  }
  next(): number {
    if (--this.left <= 0) {
      this.target = this.rng.bipolar()
      this.left = Math.max(1, this.periodSec * this.sr * (0.5 + this.rng.next()))
    }
    this.v += (this.target - this.v) * this.coef
    return this.v
  }
}

/** Zero-delay-feedback state-variable filter (lowpass + bandpass outputs). */
export class Svf {
  lp = 0
  bp = 0
  private ic1 = 0
  private ic2 = 0
  private a1 = 0
  private a2 = 0
  private a3 = 0
  private k = 1
  constructor(private sr: number) {}
  set(fc: number, q: number) {
    const f = Math.min(Math.max(fc, 10), this.sr * 0.45)
    const g = Math.tan((Math.PI * f) / this.sr)
    this.k = 1 / q
    this.a1 = 1 / (1 + g * (g + this.k))
    this.a2 = g * this.a1
    this.a3 = g * this.a2
  }
  process(x: number) {
    const v3 = x - this.ic2
    const v1 = this.a1 * this.ic1 + this.a2 * v3
    const v2 = this.ic2 + this.a2 * this.ic1 + this.a3 * v3
    this.ic1 = 2 * v1 - this.ic1
    this.ic2 = 2 * v2 - this.ic2
    this.lp = v2
    this.bp = v1
  }
}

/** Smooths each control towards its target so changes never click. */
export class ParamSmoother {
  a = 0
  b = 0
  c = 0
  private primed = false
  private coef: number
  constructor(sr: number, seconds: number) {
    this.coef = onePoleCoef(seconds, sr)
  }
  step(p: GeneratorParams) {
    if (!this.primed) {
      this.a = p.a
      this.b = p.b
      this.c = p.c
      this.primed = true
      return
    }
    this.a += (p.a - this.a) * this.coef
    this.b += (p.b - this.b) * this.coef
    this.c += (p.c - this.c) * this.coef
  }
}

/**
 * A bank of sine oscillators advanced by complex rotation (4 multiplies per sample instead of a Math.sin call).
 * Frequencies are set at control rate; call `normalize()` periodically so rounding never makes the amplitude drift.
 */
export class RotorBank {
  private c: Float64Array
  private s: Float64Array
  private cd: Float64Array
  private sd: Float64Array
  constructor(
    readonly n: number,
    rng: Rng,
  ) {
    this.c = new Float64Array(n)
    this.s = new Float64Array(n)
    this.cd = new Float64Array(n).fill(1)
    this.sd = new Float64Array(n)
    for (let i = 0; i < n; i++) {
      const ph = rng.next() * TWO_PI
      this.c[i] = Math.cos(ph)
      this.s[i] = Math.sin(ph)
    }
  }
  setFreq(i: number, hz: number, sr: number) {
    const w = (TWO_PI * hz) / sr
    this.cd[i] = Math.cos(w)
    this.sd[i] = Math.sin(w)
  }
  /** Advance oscillator i one sample and return sin(phase). */
  step(i: number): number {
    const c = this.c[i]!
    const s = this.s[i]!
    const cd = this.cd[i]!
    const sd = this.sd[i]!
    const ns = s * cd + c * sd
    this.c[i] = c * cd - s * sd
    this.s[i] = ns
    return ns
  }
  normalize() {
    for (let i = 0; i < this.n; i++) {
      const m = 1 / Math.hypot(this.c[i]!, this.s[i]!)
      this.c[i]! *= m
      this.s[i]! *= m
    }
  }
}
